import * as ft from 'funtypes'
import { Latitude, Longitude, nullable, objectWith } from '../validation.js'

/**
 * The raw GoingElectric scrape, exactly as `docs/apify-scrape-page.js` emits it.
 *
 * This is the *input* to `tools/ge-voltage-windows.ts` and to nothing else. The
 * pipeline never reads it: 5 MB of scraped HTML-derived records has no business
 * in a Cloud Function, and the generator distils it into the far smaller
 * `table.generated.json` that does ship. The runtypes live here rather than in
 * the generator because a scrape whose shape has drifted must fail at the parse
 * with a field path, not halfway through an aggregation with `undefined is not a
 * number`.
 *
 * Every electrical field is nullable and means it. GoingElectric renders a
 * connector row from whatever the operator or a contributor filled in, so `kw`,
 * `volts`, `amps`, `evseId` and even `connector` are each independently absent
 * on some rows - 108 of 9812 in the August 2026 Poland scrape carry no voltage
 * at all. The generator drops what it cannot use and counts what it dropped.
 */

/** One connector on one charging station. GoingElectric calls it a chargepoint. */
export const GeChargepoint = objectWith(
  {
    chargepointId: ft.String,
    /** GoingElectric's own plug slug, e.g. `combo_typ2`. Coarser than `plug`. */
    connector: nullable(ft.String),
    /** The plug's display label, e.g. `Combo Typ 2 (CCS) EU`. See `./plugs.ts`. */
    plug: nullable(ft.String),
    /** Declared maximum power in kW. */
    kw: nullable(ft.Number),
    /** Maximum voltage for DC, nominal line voltage for AC. See `./table.ts`. */
    volts: nullable(ft.Number),
    /** Maximum current the connector is rated for. */
    amps: nullable(ft.Number),
    /** eMI3 EVSE id as the operator publishes it, `*`-separated. */
    evseId: nullable(ft.String),
  },
  {},
)
export type GeChargepoint = ft.Static<typeof GeChargepoint>

export const GeStation = objectWith(
  {
    index: ft.Number,
    label: nullable(ft.String),
    /** Hardware model, e.g. `Ekoenergetyka Axon Easy 120`. Free text. */
    model: nullable(ft.String),
    stationId: nullable(ft.String),
    chargepoints: ft.Array(GeChargepoint),
  },
  {},
)
export type GeStation = ft.Static<typeof GeStation>

export const GeAddress = objectWith(
  {
    /** Street *including* the house number, e.g. `Wrocławska 33B`. */
    street: nullable(ft.String),
    postcode: nullable(ft.String),
    city: nullable(ft.String),
  },
  {
    country: nullable(ft.String),
    destinationId: nullable(ft.String),
    full: nullable(ft.String),
  },
)
export type GeAddress = ft.Static<typeof GeAddress>

export const GeSite = objectWith(
  {
    url: ft.String,
    /** The numeric id in the page URL. Stable across re-slugs; see `tools/goingelectric.mjs`. */
    siteId: ft.String,
    name: nullable(ft.String),
    address: GeAddress,
    stations: ft.Array(GeStation),
  },
  {
    pageTitle: nullable(ft.String),
    stationCount: nullable(ft.Number),
    chargepointCount: nullable(ft.Number),
    /**
     * WGS84, off the page's schema.org block. Optional because scrapes taken
     * before `docs/apify-scrape-page.js` learned to read it carry no such field,
     * and the generator still has to be able to parse those.
     *
     * A site with no coordinates can still declare electrics for a connector
     * EIPA already knows about; it just cannot be placed on the map on its own.
     */
    lat: nullable(Latitude),
    lon: nullable(Longitude),
  },
)
export type GeSite = ft.Static<typeof GeSite>

export const GeScrape = ft.Array(GeSite)
export type GeScrape = ft.Static<typeof GeScrape>
